// ── WHICH INSURED FIELDS ARE ACCOUNTED FOR ───────────────────────────────────
//
// Kevin, 2026-10-01: *"As we get silage appraisals in, can we tie the silage appraisal to
// the appropiate insured CLU. The goal here is to identify which fields are appraised, and
// then if we have JD data for that particular farm we can identify which fields are
// appraised, which are harvested by combine, and then we should be able to identify- are
// there any missing fields (a field that has neither JD data or an appraisal). Then we will
// also be able to see, are there fields that were appraised but then also harvested by
// combine, in this case we would use the yield data rather than appraisal."*
//
// That is four states per insured field, and each one is a different action:
//
//   APPRAISED ONLY   the adjuster valued standing crop; that is the production of record
//   HARVESTED ONLY   the combine measured it; the machine figure is the evidence
//   BOTH             ⚠ THE APPRAISAL IS SUPERSEDED. An appraisal is a pre-harvest ESTIMATE
//                    of what a field would make; once it goes through a combine the machine
//                    has measured what actually came off. Counting both double-counts the
//                    field, and taking the appraisal over the yield reports an estimate in
//                    place of a measurement. Kevin's call, and it is also the direction RMA
//                    settles on: an appraisal stands only where the crop was not harvested.
//   NEITHER          ⚠ THE FINDING. An insured field with no appraisal and no machine pass
//                    is unaccounted for — nobody has said what it made. On a claim that is
//                    a hole in the production report; off one it is a field nobody checked.
//
// ⚠ AND THE FIFTH STATE IS AN APPRAISAL THAT NAMES A FIELD WE CANNOT PLACE. It is COUNTED
// AND NAMED, never dropped: measured 2026-10-01 over all 20 CY2026 appraisal rows, only 9
// join to an insured field at all, and some of those joined to the WRONG CROP because FSN +
// field is not unique across crops. An appraisal silently discarded is production the report
// never mentions, which is the same failure as a missing field wearing a tidier face.

"use strict";

const n = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
const up = (v) => String(v == null ? "" : v).trim().toUpperCase();
const k = (v) => String(v == null ? "" : v).trim();

// ⚠ THE CROP IS PART OF THE KEY. A first measurement joined on (fsn, field) alone and
// matched a CORN silage appraisal onto a WHEAT CLU — FSN 7661 field 1 exists for both crops
// on the same farm. A match that lands on the wrong crop is worse than no match: it reports
// corn production against a wheat field and both figures become wrong.
// ⚠⚠ THE ADJUSTER'S FIELD NUMBER IS NOT FSA'S FIELD NUMBER WHEN THE APPRAISAL CARRIES NO
// TRACT — it is the line number on their own worksheet. Measured 2026-10-01 over every
// CY2026 appraisal, and it is not a tendency, it is 0 for 6:
//
//     FSN 4912  appraisal "1"        -> FSA field 16     7.3 ac = 7.3 ac
//     FSN 7663  appraisal "1" / "2"  -> FSA fields 16/5  42.0 and 13.3, both exact
//     FSN 8434  appraisal "1"/"2"/"3"-> FSA fields 5/6/10  6.0, 18.6, 10.9, all exact
//
// Every one of those rows also carries `field_name` = "1" / "2" / "3" — the adjuster wrote
// the line number into the name. So on a farm that happens to HAVE fields numbered 1..5 the
// field number matches by coincidence, and the acreage is what exposes it: on Rogers' FSN
// 7661 all five of those "matches" were wrong, including a 32.4-acre appraisal placed on a
// 16.1-acre field.
//
// THE ACREAGE IS THEREFORE THE KEY, AND THE FIELD NUMBER IS ONLY TRUSTED WHEN THE APPRAISAL
// ALSO CARRIES A TRACT — a tract means somebody keyed that row to FSA rather than numbering
// a worksheet. Both of Lang's tract-bearing rows match on all three parts AND agree on
// acreage to the hundredth, which is what a real key looks like.
const MATCH = Object.freeze({
  exact: "fsn+tract+field",
  // ⚠ ONLY WHEN IT IS UNIQUE, AND ONLY ON AN EXACT FIGURE. Two 8-acre fields on one farm
  // make the acreage say nothing, and an appraisal covering PART of a field matches nothing —
  // both stay unplaced rather than being guessed at.
  acres: "fsn + exact acreage (the appraisal's field number is its own worksheet line)",
  prefix: "fsn+tract+field prefix (FSA splits one field into 1A/1B; the appraisal names '1')",
  // ⚠ THE FOURTH TIER IS A FARM NUMBER, NOT A FIELD, AND THE RECONCILIATION IS WHAT EARNS IT.
  // Measured 2026-10-01, per farm number, appraised acres against insured acres of the same
  // crop: 4912 7.3=7.3 · 7663 55.3=55.3 · 8434 35.5=35.4 · 8435 7.5=7.5 · 8809 130.7=130.6 ·
  // 1543 12.9=12.9 · 6142 23.8=23.8. So the appraisals are COMPLETE on those farms and the
  // only thing missing is which worksheet line covers which field — one line rolls several
  // insured fields together (8809's four lines cover more than four fields).
  //
  // When the two totals tie, every insured field on that farm number IS appraised, and saying
  // so is a reading of the acres rather than a guess. What is NOT claimed is a per-field
  // quantity: the worksheet does not split it, so nothing is apportioned and the bushels stay
  // where the appraisal put them, at the farm number.
  //
  // ⚠ IT IS ONLY TAKEN WHEN THE TOTALS TIE. Appraised BELOW insured means part of the farm was
  // appraised and the field-level matches stand alone (Rogers' FSN 7661: 71.7 of 294.4 ac);
  // appraised ABOVE insured is an acreage finding and must be reported as one, never absorbed.
  farm: "farm number (the worksheet line covers several fields; the farm's acres reconcile)",
  // ⚠ THE UNIT IS THE AIP'S OWN KEY AND THE WORKSHEET PRINTS IT ON EVERY HEADER — including
  // the headers where it leaves the FSA farm number BLANK because the unit spans several
  // farms. RED DAWN FARMING LLC CY2026: 60 of 71 lines sit under such a header. Without this
  // tier they place nowhere; with it, an exact acreage that is unique WITHIN THE UNIT places
  // the line on the one field it can be — the same uniqueness rule as the farm-number tier,
  // scoped to a key the adjuster actually wrote down.
  unitAcres: "unit + exact acreage (the worksheet names the unit; its FSA farm is blank)",
  // And the unit's own reconciliation, for the same reason the farm-number tier exists.
  unit: "unit (the worksheet line covers several fields; the unit's acres reconcile)",
  // ⚠ TWO EQUAL FIELDS NAME NO FIELD — UNLESS EXACTLY ONE OF THEM WAS HARVESTED. An adjuster
  // appraises ground that is about to be (or has been) chopped; a tied field with no pass on it
  // at all is the weaker candidate. Only taken when exactly one of the tied fields has a pass,
  // and flagged for the adjuster to confirm. KINGSLEY line 70 (13.3 ac): 1583/2 was chopped
  // Oct 1, 5455/11 (13.29 ac) has no pass.
  acresHarvest: "fsn + exact acreage, tie broken by the one field with a harvest pass (adjuster to confirm)",
  // ⚠ A LINE THAT LISTS SEVERAL FIELDS, MATCHED TO ONE JOHN DEERE FIELD'S WHOLE SET OF INSURED
  // FIELDS ON THE LINE'S FARM NUMBER. Acres alone are not unique (19 six-field subsets of FSN
  // 5178 fit KINGSLEY's 37.8 ac line); the JD field's complete CLU set is one set. Placed at
  // farm level (no per-field bushels) and flagged for the adjuster to confirm.
  fieldSet: "farm number + the planted acres of one John Deere field's insured fields (adjuster to confirm)",
});
// How far an FSN's appraised total may sit from its insured total and still be the same
// ground. The measured gaps are 0.0–0.1 ac on farms up to 130 ac, so this is loose enough
// for two documents rounding and far too tight to absorb a missing field.
const FSN_TOL = (insured) => Math.max(0.5, insured * 0.0025);
// How close two acreages must be to be the same field. Tight on purpose: FSA and the
// adjuster both work to 0.01 ac, so a real match is exact and a near-miss is a different
// field, not a rounding.
const ACRE_TOL = 0.051;
// ⚠ AN APPRAISAL CANNOT COVER MORE GROUND THAN THE FIELD IT SITS ON. It values standing
// crop on part or all of a field, so appraised <= insured always holds, and a match that
// breaks it is a wrong match however well the key fits. This is the test that caught the
// FSN 7661 collision, so it REFUSES the match rather than merely flagging it — half an acre
// of slack for the two documents rounding differently, and nothing beyond that.
const OVER_TOL = 0.51;
// Reading order: the field that needs a DECISION first (both — which figure?), then the ones
// that are settled, then the ones nothing has spoken for.
// ⚠ A COMBINE PASS WHOSE MAP FAILED TO BUILD IS NOT "NOTHING YET", and reading it that way
// is what sent Kevin looking for a rip-up that never happened. BRIDGEWATER FARMING's two
// unaccounted CY2026 wheat fields (9485/1022/1 and 9309/7102/3, 151.1 ac) are both covered by
// ONE John Deere field — 180-Tim Creek — which carries a 2026-07-08 wheat harvest of 149.08 ac
// at a stated 118.6 bu/ac, synced and sitting in jd_field_operations the whole time. What is
// missing is only the yield GRID: the shapeFileAsync export bundles every layer John Deere
// holds for that pass (elevation, fuel rate, speed, moisture, wet mass, yield, contour), which
// makes a 149-acre wheat field a 358 MB download, past MAX_EXPORT_BYTES.
//
// "Nothing yet" says the combine has not been there. It had. Measured book-wide 2026-10-01:
// 728 harvest operations over 6,715 acres state a yield and an area and have no grid.
const STATE_ORDER = Object.freeze({ both: 0, both_chopped: 0, both_unknown: 0, appraised: 1, harvested: 2,
                                     chopped: 3, harvest_unknown: 3, unmapped: 4, neither: 3, pp: 5,
                                     silage_chopped: 2, silage_none: 3 });

/**
 * coverageState(hasAppraisal, hasHarvest) — the four states, and which figure to use.
 *
 * ⚠ ONE RULE, BECAUSE TWO SURFACES ASK IT. The Claims Manager asks per field to build a
 * production report; the Field Mapper asks per field to colour a polygon. If each decided
 * `both` for itself, the map could show a field in the appraisal colour while the report
 * counted its yield — and this codebase has already paid for a rule defined twice (three news
 * handlers, two guarantee engines, three copies of normFarmerName).
 *
 * `use` is the MACHINE on `both`: an appraisal estimates what standing crop would make, and
 * the combine then measured what it did.
 */
function coverageState(hasAppraisal, hasHarvest, hasUnmappedPass, isWhollyPreventPlant, harvest) {
  // ⚠ PREVENT PLANT IS SETTLED GROUND, NOT A MISSING FIGURE, and it outranks everything
  // below it because there is nothing on these acres for anything below it to find. Nobody
  // appraises a prevent-planted acre and no combine crosses one, so under the old states a
  // wholly prevent-planted field could only ever read "nothing yet" — which is the panel
  // telling an agent to go and chase production that cannot exist.
  //
  // MULLIGAN FAMILY FARM CY2026, FSA farm 7108: 27.7 insured acres, every one of them
  // prevent plant, two fields. The donut called them a hole and the farm read 91% accounted
  // on a crop where every planted acre had been walked. Kevin: *"If every field was
  // appraised, it needs to show 100% accounted ... or else its misleading."*
  //
  // ⚠ IT IS ONLY TAKEN WHERE THE WHOLE FIELD IS PP. A field that is part prevent plant has
  // planted ground on it, and that ground still needs a figure — folding it in here would
  // hide a real hole behind a settled label, which is this rule running backwards.
  // `fsnCoverage` draws the same line with its own `allPp`.
  if (isWhollyPreventPlant && !hasAppraisal && !hasHarvest) {
    return { state: "pp", use: null, supersededAppraisal: false };
  }
  // ⚠ A MAPPED PASS ALWAYS OUTRANKS AN UNMAPPED ONE. The grid measures this CLU; the pass
  // states a figure for the whole JD field, which may cover several. Where both exist the
  // grid answers and the pass is not mentioned, or one field would be counted twice.
  // ⚠⚠ APPRAISED AND CHOPPED IS NOT APPRAISED AND COMBINED (Kevin, 2026-10-26): *"If it's
  // insured as grain and then gets harvested as grain we can use the combine data, but if it's
  // insured as grain and gets chopped for silage, we need to use the appraisal."* A chopper's
  // figure is wet SILAGE tonnage; on a grain policy it is not grain production and must never
  // be reported or added as such. RED DAWN FARMING LLC CY2026: every one of its 2026 corn
  // harvest passes is labelled CORN SILAGE in t/ha with no dry yield, and all 191 insured
  // fields are type 016 grain — so "both — yield wins" was reporting silage tonnage over the
  // adjuster's grain appraisal on 71 fields.
  //
  // `harvest.form` is decided by harvestFormOf() from the John Deere pass itself. Where it
  // cannot be decided the APPRAISAL is used (the safe side of a grain claim) and the state
  // says so.
  //
  // ⚠⚠ INSURED SILAGE (corn type 026) IS ITS OWN LINE, NEVER GRAIN (Kevin, 2026-11-03):
  // *"Just make sure it can differentiate between silage and grain yields, we never merge
  // those"*, *"If they are insured as silage and chopped, we can use the JD chopper yield
  // data"*, and *"if they are insured as silage they will not be reported in bushels"*.
  // The policy says the same: grain and silage are TYPES within one unit, guaranteed and
  // counted per type (7 CFR 457.113 §12(b)), silage in tons as harvested (Corn LASH
  // FCIC-25080 ¶14), and there is no tons-to-bushels conversion to add one to the other.
  // HARKINS DAIRY CY2026 is why: its 230 silage-insured acres carried chopper passes, so they
  // read "combined" and ~7 "bu/ac" of dry-matter tonnage sat in the grain bushels.
  //   chopped          → silage_chopped: the JD chopper tonnage is the SCREENING figure (a
  //                      machine estimate, never "production to count"); a bushel appraisal
  //                      on the field is flagged as the wrong unit, never used.
  //   anything else    → silage_none: no silage figure yet. A bushel appraisal is flagged,
  //                      never the figure; a combine pass is not a silage figure either
  //                      (LASH ¶11(5)(b): silage-insured corn combined as grain is appraised
  //                      as silage before harvest).
  if (harvest && harvest.silageInsured) {
    const chopped = hasHarvest && harvest.form === "chopped";
    const sState = chopped ? "silage_chopped" : "silage_none";
    return {
      state: sState, use: USE_OF_STATE[sState] === undefined ? null : USE_OF_STATE[sState],
      supersededAppraisal: false,
      // A combine (or undeterminable) pass on silage-insured acres is not a silage figure.
      machineSetAside: hasHarvest && !chopped,
      // silage_appraisals stores BUSHELS; on silage-insured acres that is the adjuster's
      // mistake (Kevin, 2026-11-03), so it is flagged and kept out of every total.
      wrongUnitAppraisal: !!hasAppraisal,
    };
  }
  // ⚠ A CHOPPER PASS IS NEVER BUSHELS, WHATEVER THE INSURED TYPE. Where the type is not stated
  // the old rule (machine figure) stands for a COMBINED pass, but a chopped one is set aside
  // exactly as on a grain policy — its figure is dry-matter tonnage. No CY2026 corn field is
  // untyped (checked 2026-11-03), so this changes no figure today; it closes the door.
  const form = harvest && harvest.form ? harvest.form : "unknown";
  const grainRule = !!(harvest && harvest.grainInsured) || form === "chopped";
  let state;
  if (hasAppraisal && hasHarvest) {
    state = !grainRule || form === "combined" ? "both"
      : form === "chopped" ? "both_chopped" : "both_unknown";
  } else if (hasHarvest) {
    // A grain-insured field chopped with NO appraisal has no grain figure at all: the
    // tonnage is not one. It is a hole, named, not "harvested".
    state = !grainRule || form === "combined" ? "harvested"
      : form === "chopped" ? "chopped" : "harvest_unknown";
  } else {
    state = hasAppraisal ? "appraised"
      // ⚠ BELOW an appraisal, too: an appraisal values THIS field and the pass does not.
      : hasUnmappedPass ? "unmapped"
      : "neither";
  }
  return {
    state,
    // ⚠ `use` IS WHICH FIGURE TO REPORT, AND AN UNMAPPED PASS SUPPLIES NONE. It is evidence
    // the combine was there, not a quantity for this field — see the note on `unmappedPass`.
    // Returning "unmapped" here would put a field-level average on a per-field line.
    use: USE_OF_STATE[state] === undefined ? null : USE_OF_STATE[state],
    // Only a COMBINED pass supersedes an appraisal. A chopped one never does.
    supersededAppraisal: state === "both",
    // The machine figure is on the row but is NOT production of record on these states.
    machineSetAside: state === "both_chopped" || state === "both_unknown"
      || state === "chopped" || state === "harvest_unknown",
    wrongUnitAppraisal: false,
  };
}

// RMA corn type 026 = silage (GSS). The only insured type Kevin's grain rule does not cover.
const SILAGE_TYPE = "026";
// ⚠ GRAIN AND SILAGE NEVER MEET (Kevin: "we never merge those"; 7 CFR 457.113 §12(b)). A 016
// worksheet line is placed, reconciled and allocated only against 016 fields, a 026 line only
// against 026 fields. A blank type on either side says nothing, so it matches as before (the
// Field Mapper's appraisal read carried no type until 2026-11-03; no CY2026 corn field is untyped).
const typeClass = (code) => { const c = k(code); return !c ? null : c === SILAGE_TYPE ? "silage" : "grain"; };
const typeOk = (cls, f) => { const fc = typeClass(f && f.typeCode); return !cls || !fc || fc === cls; };
// The field numbers a worksheet line lists: "35,36,41,42,43,44" / "3, 4, 5, 6, 7, 8, 9" /
// "193 & 194" -> 6 / 7 / 2. A single number (or none) is one field.
const listedFieldCount = (fieldNo) => {
  const parts = String(fieldNo == null ? "" : fieldNo).split(/,|&|\band\b/i).map((x) => x.trim()).filter(Boolean);
  return parts.length;
};
// Field ids for a sentence: 4,5,6,7,8,9,10 -> "4–10"; 1,2,7 -> "1, 2, 7".
const fieldIdsText = (ids) => {
  const nums = ids.map((x) => Number(x));
  if (nums.length > 2 && nums.every(Number.isInteger)) {
    const s = [...nums].sort((a, b) => a - b);
    if (s.every((v, i) => i === 0 || v === s[i - 1] + 1)) return `${s[0]}–${s[s.length - 1]}`;
  }
  return ids.join(", ");
};
// A combined pass drier than this is shelled corn; STRICTLY above it, the field is flagged as a possible
// high-moisture-corn field. 28.0 is not flagged. The flag never sets the HMC switch.
const HMC_MOISTURE_PCT = 28;
// ⚠ ONE SETTING FOR "THE WHOLE FIELD WAS HARVESTED" (Claims Manager, `partialHarvest` on). Kevin,
// 2026-11-03: "Fully harvested over 90% sure, maybe even a lower percentage". A field whose passes
// of one method (combine, or chopper on grain- or silage-insured ground) cover at least this share
// of its planted acres counts as harvested by that method across the whole field: the remainder is
// not shown as "no figure", and the bushels are NOT scaled up — they stay what the machine measured.
// Below it, the pass values only the acres it covered and the rest reads "partly harvested".
const FULL_HARVEST_SHARE = 0.9;
// ⚠ A TINY PASS IS STILL NOT A HARVEST. A chopper (or undetermined) pass over less than this share
// of the planted acres does not make the field chopped (HARKINS 5024/18, 5024/78: 0.1 and 0.5 ac).
// A combine sliver keeps its real bushels on the acres it covered and never makes a field whole.
const SLIVER_SHARE = 0.5;
// Which figure each state reports. A state not listed reports none.
const USE_OF_STATE = Object.freeze({
  appraised: "appraised", harvested: "harvested", both: "harvested",
  both_chopped: "appraised", both_unknown: "appraised",
  // Silage-insured and chopped: the chopper's tonnage, in TONS, on the silage line only.
  silage_chopped: "harvested",
});
// The states whose acres are ACCOUNTED FOR (something valued them). `chopped` and
// `harvest_unknown` are not: on a grain policy a chopper's tonnage values nothing.
// `silage_chopped` is accounted on the SILAGE line (a JD chopper estimate, in tons);
// `silage_none` is not — a bushel appraisal values nothing on silage-insured acres.
const ACCOUNTED_STATES = Object.freeze(["appraised", "harvested", "both", "both_chopped", "both_unknown", "pp",
                                        "silage_chopped"]);

// harvestFormOf / combineForms live in harvestForm.js — one rule, shared with
// productionEvidence's machine lane.
const { harvestFormOf, combineForms, silageWetFromDm } = require("./harvestForm.js");

// "0002" + "0057" on a policy field, "0002-0057-000" on an appraisal — the same unit. The
// key is productionEvidence's own (unitKey.js), so the field tier here and the county rung
// there can never disagree about which unit a row is on.
const { pfUnitKey, apprUnitKey, unitLabel } = require("./unitKey.js");

const fieldKeys = (fsn, tract, field) => ({
  exact: [k(fsn), k(tract), up(field)].join("|"),
  // No fsn+field key — see the note by `byExact` for why that tier was removed rather than
  // demoted. Keeping the key around is how it gets wired back in by somebody tidying up.
});

/**
 * buildFieldCoverage({ crop, cropYear, policyFields, appraisals, machineRows })
 *
 *   policyFields — policy_fields rows (the INSURED fields: clu_id, fsn, tract, field_id, acres)
 *   appraisals   — silage_appraisals rows (fsn, tract, field_no, appraised_acres, appraised_bu)
 *   machineRows  — jd_field_ftf rows, metric='yield' (clu_id, acres, mean_value, jd_field_id)
 *   machineOps   — jd_field_operations harvest rows (jd_operation_id, jd_field_id, area_acres,
 *                  dry_yield, yield_unit, field_name, crop, operation_start). A pass whose
 *                  yield MAP failed to build has no machineRows and would otherwise read as
 *                  though the combine had never been there.
 *
 * Nothing here fetches, and nothing here decides a claim — it reports which fields are
 * accounted for, by what, and names everything it could not place.
 */
function buildFieldCoverage({ crop, cropYear, policyFields = [], appraisals = [], machineRows = [],
                             machineOps = [], hmcFields = [], partialHarvest = false,
                             claimMatching = false } = {}) {
  // Two Claims Manager options, both OFF for the Field Mapper (its map was replayed unchanged
  // over all 27 CY2026 claim farms with them off, 2026-11-03):
  //   `partialHarvest` — the acre split (see `machineShare` below). The Field Mapper feeds
  //                      synthetic one-acre machine rows; with it on every field reads as a sliver.
  //   `claimMatching`  — planted-acre and harvest-pass tie-break matching, the John Deere
  //                      field-set tier, the header/evidence flag, and reconciliation keyed on
  //                      (farm number, unit, type). Turning it on for the map is Kevin's call:
  //                      it re-colours 6 CLUs on 4 farms (claims-corn-combined-2026-11-03.md).
  const CROP = up(crop);
  const CY = Number(cropYear);

  // ── the insured fields, which are the ROWS of this answer ──────────────────
  // Counties and fields come from the ACREAGE REPORT, so a field with no evidence of any
  // kind still appears and reads NEITHER rather than vanishing. That is the whole point:
  // the missing field is the finding, and a list built from the evidence can never show it.
  const fields = new Map();
  for (const p of policyFields) {
    if (Number(p.crop_year) !== CY) continue;
    if (up(p.crop).indexOf(CROP) < 0) continue;
    const id = k(p.clu_id);
    if (!id) continue;
    const e = fields.get(id) || {
      cluId: id, fsn: k(p.fsn), tract: k(p.tract), fieldId: k(p.field_id),
      unitKey: pfUnitKey(p),
      // The policy the field is reported on: a unit number is only unique within a policy, so the
      // Claims page's yield-per-unit table (unitYield.js) needs it to put a field on its unit.
      policyId: p.policy_id || null,
      county: up(p.county), insuredCounty: up(p.insured_county), crop: up(p.crop), insuredAcres: 0, ppAcres: 0,
      // The insured TYPE decides whether the grain rule applies (see coverageState): 016
      // grain, 026 silage. First stated wins; a later row may fill a blank, never overwrite.
      typeCode: k(p.type_code),
      appraised: null, harvested: null,
    };
    if (!e.typeCode && k(p.type_code)) e.typeCode = k(p.type_code);
    e.insuredAcres += n(p.acres) || 0;
    // ⚠ PREVENT PLANT, CARRIED PER FIELD. Nothing grew on these acres, so nobody can
    // appraise them and no combine will cross them — they are SETTLED, not missing. Without
    // this the rule had no way to tell "nobody has valued this field" from "there was never
    // anything on it to value", and the second reads as the first.
    e.ppAcres += Math.min(n(p.acres) || 0, Math.max(0, n(p.pp_acres) || 0));
    fields.set(id, e);
  }

  // ── machine passes, by CLU ─────────────────────────────────────────────────
  // The yield UNIT is on the operation, not the per-CLU row; it cross-checks the crop label in
  // harvestFormOf(). A row whose operation is not supplied is judged on its label alone.
  // ⚠ HIGH-MOISTURE CORN IS A HUMAN DECISION, NEVER A DERIVATION (Kevin, 2026-10-27). A combine can
  // harvest high-moisture corn that is fed on the farm and never sold as grain; on a grain-insured
  // unit that field is valued by its APPRAISAL exactly like a chopped one. John Deere data cannot tell
  // it from wet shelled corn, so the only thing that makes a field HMC is a stored switch
  // (claim_hmc_fields, keyed by the field's own clu id). The moisture flag below only POINTS at
  // candidates and never sets it.
  const hmcByKey = new Map();
  for (const h of hmcFields || []) { const id = k(h && h.field_key); if (id) hmcByKey.set(id, h); }
  const moistOfOp = new Map();
  for (const o of machineOps) { const id = k(o.jd_operation_id), mo = n(o.moisture_pct); if (id && mo != null && mo > 0) moistOfOp.set(id, mo); }
  const unitOfOp = new Map();
  for (const o of machineOps) { const id = k(o.jd_operation_id); if (id && o.yield_unit != null) unitOfOp.set(id, o.yield_unit); }
  for (const r of machineRows) {
    if (r.metric !== "yield") continue;
    if (up(r.crop).indexOf(CROP) < 0) continue;
    const y = r.operation_date ? new Date(r.operation_date).getUTCFullYear() : null;
    if (y !== CY) continue;
    const ac = n(r.acres), mv = n(r.mean_value);
    if (!(ac > 0) || !(mv > 0)) continue;
    const id = k(r.clu_id);
    const f = id ? fields.get(id) : null;
    // A machine pass on ground this crop does not insure is not this list's business —
    // productionEvidence already counts and names those.
    if (!f) continue;
    f.harvested = f.harvested || { acres: 0, quantity: 0, passes: 0, forms: [], moisture: null, chopAcres: 0, chopDm: 0, combAcres: 0, combQty: 0 };
    { const mo = moistOfOp.get(k(r.jd_operation_id)); if (mo != null) f.harvested.moisture = f.harvested.moisture == null ? mo : Math.max(f.harvested.moisture, mo); }
    f.harvested.acres += ac;
    f.harvested.quantity += ac * mv;
    f.harvested.passes++;
    const passForm = harvestFormOf({ crop: r.crop,
      yield_unit: unitOfOp.has(k(r.jd_operation_id)) ? unitOfOp.get(k(r.jd_operation_id)) : r.yield_unit });
    f.harvested.forms.push(passForm);
    // A chopper pass's figure is DRY-MATTER tons (harvestForm.js silageWetFromDm), carried
    // apart so the silage line states it in tons and nothing reads it as bushels.
    if (passForm === "chopped") { f.harvested.chopAcres += ac; f.harvested.chopDm += ac * mv; }
    // The combine's own share, kept apart for a field the combine AND the chopper crossed.
    if (passForm === "combined") { f.harvested.combAcres += ac; f.harvested.combQty += ac * mv; }
  }

  // ── a pass whose map failed to build ───────────────────────────────────────
  // ⚠ IT IS PLACED, AND ITS BUSHELS ARE NOT SPLIT. The operation states ONE figure for the
  // whole John Deere field, and that field may cover several insured CLUs — 180-Tim Creek
  // covers two. Splitting 149.08 ac × 118.6 bu/ac across them pro-rata would print a number
  // per field that nothing measured, on a line that reads exactly like a gridded one. The
  // repo's own rule for this shape is "sum the units; don't model the split" — and here the
  // units are not there, so nothing is modelled. The pass is named, its own totals are stated
  // once, and the split is left to the grid that has not been built.
  //
  // ⚠ PLACEMENT IS BY THE FIELD'S OWN HISTORY, NOT BY A BORROWED ACRE SPLIT. Measured
  // 2026-10-01: of 1,678 John Deere fields with more than one gridded pass, 565 (34%) have a
  // DIFFERENT CLU set from one pass to the next, because attribution is by area overlap and
  // the machine does not drive the same ground twice. So another pass's acre split is not a
  // sound basis for this one — but "this ground has been attributed to this JD field before"
  // is a true statement either way, and it is all that is claimed.
  const ftfFieldOfClu = new Map();     // clu -> Set(jd_field_id), from ANY pass, ANY year
  for (const r of machineRows) {
    const id = k(r.clu_id), fid = k(r.jd_field_id);
    if (!id || !fid) continue;
    if (!ftfFieldOfClu.has(id)) ftfFieldOfClu.set(id, new Set());
    ftfFieldOfClu.get(id).add(fid);
  }
  const unmappedPasses = [], unplacedPasses = [];
  const griddedOps = new Set(machineRows.map((r) => k(r.jd_operation_id)).filter(Boolean));
  for (const o of machineOps) {
    if (up(o.crop).indexOf(CROP) < 0) continue;
    const y = o.operation_start ? new Date(o.operation_start).getUTCFullYear() : null;
    if (y !== CY) continue;
    // ⚠ A PASS THAT DID BUILD ITS MAP IS ALREADY COUNTED ABOVE. Admitting it here would show
    // the same acres twice, once measured and once stated.
    if (griddedOps.has(k(o.jd_operation_id))) continue;
    const ac = n(o.area_acres), per = n(o.dry_yield);
    // A pass that states no yield and no area says nothing a reader can act on.
    if (!(ac > 0) || !(per > 0)) continue;
    // ⚠ AND NEITHER DOES ONE WHOSE PRODUCTION ROUNDS TO NOTHING. Found by rendering this
    // against Bridgewater: a 0.02-acre pass on 131-Waterstone South at 6.1 bu/ac printed as
    // "0 ac × 6.1 bu/ac = 0" in the banner and attached itself to a field that already had
    // a measured grid — a line of noise beside the one pass that mattered. The floor is the
    // QUANTITY rather than the acres, because that is the thing being claimed; a real
    // sliver-acre pass with real bushels on it still reports.
    if (Math.round(ac * per) < 1) continue;
    const fid = k(o.jd_field_id);
    const pass = {
      opId: k(o.jd_operation_id), fieldId: fid, fieldName: o.field_name || null,
      date: o.operation_start || null, acres: +ac.toFixed(1), perAcre: per,
      unit: o.yield_unit || null, quantity: Math.round(ac * per),
    };
    const covered = fid ? [...fields.values()].filter((f) => (ftfFieldOfClu.get(f.cluId) || new Set()).has(fid)) : [];
    if (!covered.length) {
      // Named and counted, never dropped. 181 of 271 such passes book-wide land here because
      // their JD field has never had a gridded pass at all, so nothing ties it to a CLU.
      unplacedPasses.push({ ...pass, why: fid ? "no insured field of this crop has ever been attributed to that John Deere field"
                                              : "the operation carries no John Deere field id" });
      continue;
    }
    unmappedPasses.push({ ...pass, fieldCount: covered.length });
    for (const f of covered) {
      f.unmappedPass = f.unmappedPass || { passes: 0, acres: 0, quantity: 0, list: [] };
      f.unmappedPass.passes++;
      f.unmappedPass.acres += pass.acres;
      f.unmappedPass.quantity += pass.quantity;
      f.unmappedPass.list.push(pass);
    }
  }

  // ── appraisals, joined to a field ──────────────────────────────────────────
  // ⚠ THERE IS DELIBERATELY NO fsn+field MAP HERE. One existed, it was the second tier, and
  // it is the defect: with no tract the field number is a worksheet line, so that map placed
  // five of Rogers' appraisals on five wrong fields and every one looked like a clean key hit.
  // Do not add it back as a "fallback" — a wrong field with a plausible number is worse than
  // an unplaced line, because nothing downstream can tell it from a real match.
  const byExact = new Map(), byPrefix = new Map(), byFsn = new Map(), byUnit = new Map();
  for (const f of fields.values()) {
    const kk = fieldKeys(f.fsn, f.tract, f.fieldId);
    byExact.set(kk.exact, f);
    // FSA splits one field into 1A / 1B; an adjuster writing "1" means the whole of it.
    const base = up(f.fieldId).replace(/[A-Z]+$/, "");
    if (base) {
      const pk = [k(f.fsn), base].join("|");
      if (!byPrefix.has(pk)) byPrefix.set(pk, []);
      byPrefix.get(pk).push(f);
    }
    const fk = k(f.fsn);
    if (fk) { if (!byFsn.has(fk)) byFsn.set(fk, []); byFsn.get(fk).push(f); }
    if (f.unitKey) { if (!byUnit.has(f.unitKey)) byUnit.set(f.unitKey, []); byUnit.get(f.unitKey).push(f); }
  }

  // ⚠ THE ACREAGE A WORKSHEET LINE STATES IS PLANTED GROUND. An adjuster appraises what grew,
  // so a field that is part prevent plant is appraised on insured − PP (ANCHOR 5507/2118/9: 8.0
  // insured − 0.5 PP = 7.5, the line's own figure). Either figure is accepted; if the two pick
  // different fields the line names no field (still a tie).
  const plantedOf = (x) => Math.max(0, x.insuredAcres - (x.ppAcres || 0));
  const acresFit = (x, ac) => Math.abs(x.insuredAcres - ac) <= ACRE_TOL
    || (claimMatching && (x.ppAcres || 0) > 0.005 && Math.abs(plantedOf(x) - ac) <= ACRE_TOL);
  // A harvest pass of any kind on the field (gridded or not), for the tie-break.
  const hasPass = (x) => !!(x.harvested || x.unmappedPass);
  const unplacedAppraisals = [];
  // Bushel lines on SILAGE-insured ground that no 026 field accepted: flagged (wrong unit),
  // never counted, never reconciled — kept apart so they cannot reach a grain figure.
  const unplacedSilageBu = [];
  for (const a of appraisals) {
    if (Number(a.crop_year) !== CY) continue;
    if (up(a.crop).indexOf(CROP) < 0) continue;
    const qty = n(a.appraised_bu) != null ? n(a.appraised_bu) : n(a.appraised_qty);
    const ac = n(a.appraised_acres);
    const kk = fieldKeys(a.fsn, a.tract, a.field_no);
    const cls = typeClass(a.type_code);
    const fits = (x) => typeOk(cls, x);
    // ⚠ THE FIELD NUMBER IS ONLY A KEY WHEN A TRACT CAME WITH IT. See MATCH above: with no
    // tract it is the worksheet line number, and on a farm numbered 1..5 it matches the
    // wrong field while looking perfect.
    const keyed = !!k(a.tract);
    let f = keyed ? byExact.get(kk.exact) : null, how = MATCH.exact;
    if (f && !fits(f)) f = null;
    if (!f && keyed) {
      // ⚠ A PREFIX MATCH IS ONLY TAKEN WHEN IT IS UNAMBIGUOUS. "1" against both 1A and 1B
      // names two fields, and picking either is a guess about which acres were appraised.
      const cand = (byPrefix.get([k(a.fsn), up(a.field_no)].join("|")) || []).filter((x) => k(x.tract) === k(a.tract) && fits(x));
      if (cand.length === 1) { f = cand[0]; how = MATCH.prefix; }
    }
    // An appraisal cannot be bigger than the field it sits on — see OVER_TOL. A keyed match
    // that breaks that is the wrong field, so it is dropped here and the acreage gets its turn.
    if (f && ac != null && ac > f.insuredAcres + OVER_TOL) f = null;
    let tie = null;
    if (!f && ac != null && ac > 0) {
      // The acreage tier. Only ever UNIQUE, and only on an EXACT figure — see MATCH.acres.
      const onFsn = (byFsn.get(k(a.fsn)) || []).filter((x) => fits(x) && acresFit(x, ac));
      if (onFsn.length === 1) { f = onFsn[0]; how = MATCH.acres; }
      else if (onFsn.length > 1 && claimMatching) {
        // See MATCH.acresHarvest: exactly one of the tied fields has a pass, and none is taken.
        const withPass = onFsn.filter((x) => hasPass(x) && !x.appraised);
        if (withPass.length === 1 && onFsn.filter(hasPass).length === 1) { f = withPass[0]; how = MATCH.acresHarvest; }
        else tie = onFsn;
      }
    }
    const uk = apprUnitKey(a);
    // ⚠ THE UNIT TIER IS FOR A LINE WHOSE FSA FARM IS BLANK (or names no farm on the map). A line
    // that names a mapped farm number belongs to that farm: matched by acreage onto another farm's
    // field of the same size, it was counted twice — on that field AND in its own farm's farm-level
    // reconciliation (GARY SWEDE CY2026 line 9, FSN 6123 14.4 ac, also sat on 6794/5936/15;
    // ROGERS line 16, FSN 8809 28.1 ac, also sat on 7661/4863/6). Claim page only.
    const unitTierOk = !claimMatching || !k(a.fsn) || !(byFsn.get(k(a.fsn)) || []).length;
    if (!f && uk && unitTierOk && ac != null && ac > 0) {
      // The unit tier — see MATCH.unitAcres. Unique within the unit, exact, and never onto a
      // field another line has already claimed: two equal lines on one unit name no field.
      const onUnit = (byUnit.get(uk) || []).filter((x) => fits(x) && acresFit(x, ac));
      if (onUnit.length === 1 && !onUnit[0].appraised) { f = onUnit[0]; how = MATCH.unitAcres; }
      else if (onUnit.length > 1 && claimMatching) {
        const withPass = onUnit.filter((x) => hasPass(x) && !x.appraised);
        if (withPass.length === 1 && onUnit.filter(hasPass).length === 1) { f = withPass[0]; how = MATCH.acresHarvest; }
      }
    }
    if (!f) {
      // ⚠ WHY IT COULD NOT BE PLACED IS AS USEFUL AS THE FACT, because the three causes need
      // three different fixes: a missing field number is an intake gap the adjuster can close,
      // an unknown FSN means the acreage report does not carry that farm at all, and an
      // ambiguous acreage is a real question only the grower can answer.
      const onFsn = byFsn.get(k(a.fsn)) || [];
      const sameAc = ac != null ? onFsn.filter((x) => Math.abs(x.insuredAcres - ac) <= ACRE_TOL) : [];
      const biggest = onFsn.reduce((m, x) => Math.max(m, x.insuredAcres), 0);
      const entry = {
        fsn: k(a.fsn), tract: k(a.tract), field: k(a.field_no), fieldName: k(a.field_name),
        county: up(a.county), acres: ac, quantity: qty, unitKey: uk, unitLabel: unitLabel(uk),
        typeClass: cls, src: a,
        // The tied candidates, named for the reader — never a placement.
        tiedWith: tie ? tie.map((x) => `${x.fsn}/${x.tract}/${x.fieldId}`) : null,
        why: !k(a.fsn)
          ? (uk
            ? `the worksheet names unit ${unitLabel(uk)} but no farm number, and no single insured ${CROP} field on that unit is ${ac != null ? ac.toFixed(1) + " ac" : "of its acreage"}`
            : "the appraisal names no farm number")
          : !onFsn.length
            ? `no insured ${CROP} field on farm ${k(a.fsn)} this crop year`
            : sameAc.length > 1
              ? `farm ${k(a.fsn)} has ${sameAc.length} insured fields of ${sameAc[0].insuredAcres.toFixed(1)} ac — the appraisal does not say which`
              : ac != null && ac > biggest + OVER_TOL
                // This is the one that is a FINDING rather than an intake gap: an appraisal
                // covering more ground than any single insured field on that farm is either
                // several fields on one worksheet line, or acres nobody reported.
                ? `${ac.toFixed(1)} appraised ac is more than any one insured ${CROP} field on farm ${k(a.fsn)} (largest ${biggest.toFixed(1)} ac) — it may cover several fields, or acres that were not reported`
                : `no insured ${CROP} field on farm ${k(a.fsn)} is ${ac != null ? ac.toFixed(1) + " ac" : "of its acreage"}, and the appraisal carries no tract to key on`,
        fsnHasFields: onFsn.length,
      };
      if (cls === "silage") unplacedSilageBu.push(entry); else unplacedAppraisals.push(entry);
      continue;
    }
    f.appraised = f.appraised || { acres: 0, quantity: 0, lines: 0, matchedBy: how };
    if (how === MATCH.acresHarvest) f.appraised.confirm = "tie broken by the harvest pass; adjuster to confirm";
    f.appraised.acres += ac || 0;
    if (qty != null) f.appraised.quantity += qty;
    f.appraised.lines++;
    // Which worksheet lines sit on this field, so every line can be traced to one place.
    (f.appraised.lineIds = f.appraised.lineIds || []).push(a.id != null ? a.id : null);
  }

  // ── a multi-field line on ONE John Deere field's insured fields ────────────
  // See MATCH.fieldSet. The JD field's CLU set comes from THIS crop year's passes (attribution
  // moves between passes), restricted to insured fields of the line's farm number, unit and
  // type. The set must be COMPLETE (no field of it already appraised) and the only one that
  // fits; otherwise the line stays unplaced and is still counted at its unit.
  const cluSetOfJdField = new Map();   // jd_field_id -> Set(clu) this crop year
  for (const r of machineRows) {
    if (r.metric !== "yield" || up(r.crop).indexOf(CROP) < 0) continue;
    const y = r.operation_date ? new Date(r.operation_date).getUTCFullYear() : null;
    const id = k(r.clu_id), fid = k(r.jd_field_id);
    if (y !== CY || !id || !fid || !fields.has(id)) continue;
    if (!cluSetOfJdField.has(fid)) cluSetOfJdField.set(fid, new Set());
    cluSetOfJdField.get(fid).add(id);
  }
  const jdFieldName = new Map();
  for (const o of machineOps) if (k(o.jd_field_id) && o.field_name) jdFieldName.set(k(o.jd_field_id), String(o.field_name));
  const farmLevelAppraisals = [];
  const fieldSetPlaced = new Set();
  for (const u of unplacedAppraisals) {
    if (!claimMatching || !u.fsn || !(u.acres > 0) || listedFieldCount(u.field) < 2) continue;
    const hits = [];
    for (const [fid, clus] of cluSetOfJdField) {
      const set = [...clus].map((id) => fields.get(id))
        .filter((x) => x.fsn === u.fsn && (!u.unitKey || !x.unitKey || x.unitKey === u.unitKey) && typeOk(u.typeClass, x));
      if (set.length < 2 || set.some((x) => x.appraised)) continue;
      if (Math.abs(set.reduce((t, x) => t + plantedOf(x), 0) - u.acres) <= ACRE_TOL) hits.push({ fid, set });
    }
    if (hits.length !== 1) continue;
    const { fid, set } = hits[0];
    const group = "S|" + fid + "|" + u.fsn;
    for (const x of set) x.appraised = { acres: null, quantity: null, lines: 0, matchedBy: MATCH.fieldSet, atFarmLevel: true, group };
    const ids = set.map((x) => x.fieldId);
    const tracts = [...new Set(set.map((x) => x.tract))];
    farmLevelAppraisals.push({
      ...u, group, cluIds: set.map((x) => x.cluId), fieldSet: true,
      why: `matches the ${set.length} insured fields of John Deere field ${jdFieldName.get(fid) || fid} on farm ${u.fsn} (${tracts.length === 1 ? tracts[0] + "/" : ""}${fieldIdsText(ids)}); adjuster to confirm`,
    });
    fieldSetPlaced.add(u);
  }
  const stillAfterSets = unplacedAppraisals.filter((u) => !fieldSetPlaced.has(u));

  // ── farm-number coverage, where the acres reconcile ────────────────────────
  // See MATCH.farm. Nothing here apportions a bushel; it records that the farm number is
  // fully appraised, so its fields stop reading as unaccounted-for when they are not.
  // ⚠ KEYED ON (FARM NUMBER, UNIT, TYPE), NEVER THE FARM NUMBER ALONE. A farm number can span
  // units and counties: WALKER FARM's FSN 6721 is unit 2 (Livingston) AND unit 3 (Steuben), and
  // a whole-FSN reconciliation let Steuben's farm-level line spread into Livingston (+355 / −353
  // bu, and a false "not tied" note). A line with no unit printed is reconciled on the farm
  // number. Bushel lines on silage-insured ground are never reconciled (flag-only).
  const stillUnplaced = [];
  const lineCls = (a) => typeClass(a.type_code);
  // Without `claimMatching` the key is the farm number alone, as it always was.
  const fsnGroup = (fsn, uk, cls) => (claimMatching ? ["F", fsn, uk || "", cls || ""] : ["F", fsn, "", ""]).join("|");
  const apByFsn = new Map();
  for (const a of appraisals) {
    if (Number(a.crop_year) !== CY) continue;
    if (up(a.crop).indexOf(CROP) < 0) continue;
    const fk = k(a.fsn);
    if (!fk || lineCls(a) === "silage") continue;
    const g = fsnGroup(fk, apprUnitKey(a), lineCls(a));
    if (!apByFsn.has(g)) apByFsn.set(g, { fsn: fk, unitKey: claimMatching ? apprUnitKey(a) : null, cls: claimMatching ? lineCls(a) : null, acres: 0, quantity: 0, lines: 0 });
    const e = apByFsn.get(g);
    e.acres += n(a.appraised_acres) || 0;
    const q = n(a.appraised_bu) != null ? n(a.appraised_bu) : n(a.appraised_qty);
    if (q != null) e.quantity += q;
    e.lines++;
  }
  const groupFields = (fsn, uk, cls) => (byFsn.get(fsn) || [])
    .filter((f) => (!uk || f.unitKey === uk) && typeOk(cls, f));
  const reconciledFsns = new Map();
  for (const [g, ap] of apByFsn) {
    const onFsn = groupFields(ap.fsn, ap.unitKey, ap.cls);
    if (!onFsn.length) continue;
    const insured = onFsn.reduce((s, f) => s + f.insuredAcres, 0);
    // ⚠⚠ AGAINST PLANTED, AND AS COVERAGE RATHER THAN EQUALITY. Two measured defects, both
    // on MULLIGAN FAMILY FARM's CY2026 corn, which read 91% accounted while every planted
    // acre on the farm had been walked. Kevin: *"If every field was appraised, it needs to
    // show 100% accounted ... or else its misleading."*
    //
    //   FSN 7108   27.7 insured, ALL of it prevent plant, 0 planted, 0 appraised.
    //              Against INSURED it misses by the whole 27.7 and its 2 fields read
    //              "nothing yet" — on ground where there was never anything to appraise.
    //   FSN 6198   781.9 planted, 806.4 appraised — 24.5 acres MORE than exist. The old
    //              test is a two-sided |difference|, so an EXCESS failed it exactly like a
    //              shortfall, and 6 fields / 103.9 ac read as unaccounted.
    //
    // Those two farms are the whole 131.6 ac the donut was calling a hole.
    //
    // ⚠ THE EXCESS IS STILL REPORTED, NOT ABSORBED. The rule above this one says an
    // over-appraisal "is an acreage finding and must be reported as one" and that stands —
    // `excessAcres` carries it and the panel prints it. What changes is that a finding
    // ABOUT the acreage no longer makes the FIELDS read as unvalued: more appraised acres
    // than exist is proof the adjuster covered the ground, not evidence he missed it.
    // Kevin, on this farm: *"I dont know how we got more appraised acres than planted acres
    // but in this case just leave it."*
    //
    // ⚠ AND SHORT IS STILL SHORT. Rogers' FSN 7661 (71.7 appraised of 294.4) must keep
    // failing — a part-appraised farm whose fields were matched individually is exactly
    // what this tier must not rescue.
    const planted = onFsn.reduce((s, f) => s + Math.max(0, f.insuredAcres - (f.ppAcres || 0)), 0);
    if (ap.acres >= planted - FSN_TOL(planted || insured))
      reconciledFsns.set(g, { ...ap, fields: onFsn, insured, planted, excessAcres: +(ap.acres - planted).toFixed(1) });
  }
  // ⚠ COVERED AND FLAGGED ARE NOT THE SAME ANSWER, AND THIS FARM GETS BOTH. An appraisal
  // larger than the ground it was taken on is proof the adjuster walked every acre AND an
  // acreage discrepancy somebody has to explain. The old rule could only say one of those,
  // so it chose the second and left the fields reading as unvalued; this says both.
  //
  // MEASURED 2026-10-07 over every appraisal in the book — 19 (farm, FSA farm) cells across
  // 3 growers: EIGHTEEN tie to their planted acres within 0.1 ac, ONE is over (MULLIGAN's
  // 6198, 806.4 on 781.9 = 103%), and ONE is genuinely short (ROGERS' 7661, 71.7 of 294.4 =
  // 24%). So an excess is rare and small, a shortfall is real, and the two must not share a
  // test — which is exactly what a two-sided |difference| made them do.
  const overAppraisedFsns = [];
  for (const [, r] of reconciledFsns) {
    if (!(r.excessAcres > FSN_TOL(r.planted || r.insured))) continue;
    const fk = r.fsn;
    overAppraisedFsns.push({
      fsn: fk, unitKey: r.unitKey || null, planted: +r.planted.toFixed(1), appraised: +r.acres.toFixed(1),
      excessAcres: r.excessAcres, insured: +r.insured.toFixed(1),
      why: `farm ${fk} was appraised on ${r.acres.toFixed(1)} ac against ${r.planted.toFixed(1)} planted ${CROP} ac — ${r.excessAcres.toFixed(1)} ac more than are insured here. Every field on it is covered; the extra acreage is a finding for the adjuster, not production.`,
    });
  }
  // ── the same reconciliation, on the UNIT ───────────────────────────────────
  // See MATCH.unit. A line the farm-number tier could not place is tried here, so a unit
  // whose worksheet lines add up to its planted acres reads as fully appraised even where the
  // header left the farm number blank. Same coverage test as the farm-number tier, and the
  // same type key.
  const unitGroup = (uk, cls) => ["U", uk, claimMatching ? cls || "" : ""].join("|");
  const apByUnit = new Map();
  for (const a of appraisals) {
    if (Number(a.crop_year) !== CY) continue;
    if (up(a.crop).indexOf(CROP) < 0) continue;
    const uk = apprUnitKey(a);
    if (!uk || lineCls(a) === "silage") continue;
    const g = unitGroup(uk, lineCls(a));
    if (!apByUnit.has(g)) apByUnit.set(g, { unitKey: uk, cls: claimMatching ? lineCls(a) : null, acres: 0, lines: 0 });
    const e = apByUnit.get(g);
    e.acres += n(a.appraised_acres) || 0;
    e.lines++;
  }
  const reconciledUnits = new Map();
  for (const [g, ap] of apByUnit) {
    const onUnit = (byUnit.get(ap.unitKey) || []).filter((f) => typeOk(ap.cls, f));
    if (!onUnit.length) continue;
    const planted = onUnit.reduce((s, f) => s + Math.max(0, f.insuredAcres - (f.ppAcres || 0)), 0);
    if (!(ap.acres >= planted - FSN_TOL(planted))) continue;
    reconciledUnits.set(g, { ...ap, fields: onUnit, planted, excessAcres: +(ap.acres - planted).toFixed(1) });
  }
  for (const u of stillAfterSets) {
    const gF = u.fsn ? fsnGroup(u.fsn, u.unitKey, u.typeClass) : null;
    const r = gF ? reconciledFsns.get(gF) : null;
    const gU = !r && u.unitKey ? unitGroup(u.unitKey, u.typeClass) : null;
    const ru = gU ? reconciledUnits.get(gU) : null;
    if (!r && ru) {
      farmLevelAppraisals.push({
        ...u, group: gU,
        why: `the worksheet line covers several fields; unit ${unitLabel(u.unitKey)}'s ${ru.acres.toFixed(1)} appraised ac cover its ${ru.planted.toFixed(1)} planted ${CROP} ac, so every field on it is appraised`,
      });
      continue;
    }
    if (!r) { stillUnplaced.push(u); continue; }
    farmLevelAppraisals.push({
      ...u, group: gF,
      why: `the worksheet line covers several fields; farm ${u.fsn}'s ${r.acres.toFixed(1)} appraised ac cover its ${r.planted.toFixed(1)} planted ${CROP} ac${r.excessAcres > 0.05 ? ` (${r.excessAcres.toFixed(1)} ac more than are insured — an acreage finding, reported separately)` : ""}, so every field on it is appraised`,
    });
  }
  for (const [g, r] of reconciledFsns) {
    for (const f of r.fields) {
      if (f.appraised) continue;
      // No acres and no quantity on purpose — the worksheet states neither per field.
      f.appraised = { acres: null, quantity: null, lines: 0, matchedBy: MATCH.farm, atFarmLevel: true, group: g };
    }
  }
  for (const [g, r] of reconciledUnits) {
    for (const f of r.fields) {
      if (f.appraised) continue;
      f.appraised = { acres: null, quantity: null, lines: 0, matchedBy: MATCH.unit, atFarmLevel: true, group: g };
    }
  }

  // ── a line still unplaced: WHERE IT COUNTS, and what the evidence says ─────
  // ⚠ PRODUCTION IS A UNIT QUANTITY (7 CFR 457.113; Corn LASH FCIC-25080), so a line that names
  // no field is still production — counted in its worksheet unit's county by the SAME rule
  // productionEvidence files it under (appraisalsByCounty: unit, then FSA farm, then header).
  // Never guessed onto a field, never moved to another unit.
  // ⚠ AND WHERE THE HEADER DISAGREES WITH THE GROUND, IT IS FLAGGED, NOT MOVED. A line listing N
  // fields whose acres equal exactly N insured fields inside ONE harvested John Deere field —
  // and that is the only such set — names where it probably belongs. If that is another farm
  // number or unit than the header prints, the adjuster has to say which is right; until then
  // the line counts where the worksheet put it. KINGSLEY "3, 4, 5, 6, 7, 8, 9" (47.5 ac): header
  // Genesee 5178 unit 1; Wyoming 6839/1196 fields 4–10 are 47.52 ac, all chopped Oct 9.
  if (stillUnplaced.length) {
    const { appraisalsByCounty } = require("./productionEvidence.js");
    for (const u of stillUnplaced) {
      const bc = appraisalsByCounty({ appraisals: [u.src], policyFields, cropYear: CY }).byCounty;
      u.countyOfLine = [...bc.keys()][0] || u.county || null;
      const N = listedFieldCount(u.field);
      if (!claimMatching || N < 2 || !(u.acres > 0)) continue;
      const hits = [];
      for (const [fid, clus] of cluSetOfJdField) {
        const cand = [...clus].map((id) => fields.get(id)).filter((x) => !x.appraised && typeOk(u.typeClass, x));
        if (cand.length < N || cand.length > 16) continue;
        // every N-subset of this JD field's free insured fields whose planted acres fit
        (function pick(i, chosen, sum) {
          if (hits.length > 1) return;
          if (chosen.length === N) { if (Math.abs(sum - u.acres) <= ACRE_TOL) hits.push({ fid, set: chosen }); return; }
          if (i >= cand.length || sum > u.acres + ACRE_TOL) return;
          pick(i + 1, [...chosen, cand[i]], sum + plantedOf(cand[i]));
          pick(i + 1, chosen, sum);
        })(0, [], 0);
      }
      if (hits.length !== 1) continue;
      const set = hits[0].set;
      const fsns = [...new Set(set.map((x) => x.fsn))], tracts = [...new Set(set.map((x) => x.tract))];
      const cty = [...new Set(set.map((x) => x.insuredCounty || x.county))];
      const where = `${cty.join("/")} ${fsns.join("/")}${tracts.length === 1 ? "/" + tracts[0] : ""} fields ${fieldIdsText(set.map((x) => x.fieldId))}`;
      const sameHeader = fsns.length === 1 && fsns[0] === u.fsn && set.every((x) => !u.unitKey || x.unitKey === u.unitKey);
      if (sameHeader) u.likelyFields = `likely ${where}; adjuster to confirm`;
      else u.headerConflict = `header says ${u.countyOfLine || u.county || "?"}${u.fsn ? "/" + u.fsn : ""}; acreage and harvest point to ${where}. Counted as printed; adjuster to confirm`;
    }
  }

  // ── the four states ────────────────────────────────────────────────────────
  const rows = [...fields.values()].map((f) => {
    // The one decision, shared with the Field Mapper — see coverageState.
    // Wholly prevent plant: every insured acre on the field is PP. The 0.05 is the same
    // hundredth-of-an-acre slack the rest of this module uses for two documents rounding.
    const whollyPp = f.insuredAcres > 0.05 && (f.ppAcres || 0) >= f.insuredAcres - 0.05;
    const machineForm = f.harvested ? combineForms(f.harvested.forms) : null;
    // A field marked HMC with a combine pass on it is treated as CHOPPED: the appraisal is the figure
    // and the combine figure is set aside (coverageState). No pass, nothing to override.
    const hmc = hmcByKey.get(f.cluId) || null;
    // Insured GRAIN is any stated type that is not corn silage (026). No stated type: Kevin's
    // rule says nothing, so the previous behaviour stands — see coverageState.
    const grainInsured = !!f.typeCode && f.typeCode !== SILAGE_TYPE;
    const silageInsured = f.typeCode === SILAGE_TYPE;
    // ⚠ AN HMC MARK IS ONLY APPLIED ON A STATED GRAIN TYPE. The switch moves a field from its
    // combine figure to its appraisal under the GRAIN rule, and with no stated type that rule
    // does not run — so honouring the mark there would drop the combine bushels from every
    // other figure while this field kept using them. A stored mark on such a field is kept and
    // shown (hmcNotApplied), never silently obeyed or deleted.
    const hmcMarked = !!(hmc && f.harvested && grainInsured);
    const hmcNotApplied = !!(hmc && f.harvested && !grainInsured);
    const form = f.harvested ? (hmcMarked ? "chopped" : machineForm) : null;
    // Moisture is only meaningful on a COMBINED pass (a chopper's wet silage is not "high-moisture").
    const moisturePct = f.harvested && machineForm === "combined" ? f.harvested.moisture : null;
    const moistureAbove = moisturePct != null && moisturePct > HMC_MOISTURE_PCT;
    // ⚠ A PASS OVER PART OF A FIELD VALUES THAT PART ONLY (`partialHarvest`, Claims Manager). The
    // machine's own acres are the ground it measured; the rest of the field has no figure.
    // VERRATTI CY2026: a 0.1-ac combine pass (37 bu) read a 98.1-ac field as "harvested", so 98 ac
    // never showed as needing a figure. And a pass over a sliver does not make the field
    // chopped: a field where the chopper covered LESS THAN HALF the planted acres reads by its
    // majority (HARKINS 5024/18 and 5024/78 read "chopped, no appraisal" off a neighbour's 0.1 and
    // 0.5 ac). The combine's bushels and the chopper's tons are never dropped: they stand on the
    // acres they were measured on.
    const plantedF = Math.max(0, f.insuredAcres - (f.ppAcres || 0));
    const machineAc = f.harvested ? Math.min(plantedF, f.harvested.acres) : 0;
    const machineShare = f.harvested && plantedF > 0 ? machineAc / plantedF : null;
    // ⚠ A GRAIN-INSURED FIELD THE COMBINE AND THE CHOPPER BOTH CROSSED IS TWO PARTS, NOT ONE
    // "unknown" field (Kevin's rules: on 016 the combine is grain bushels, the chopped acres are
    // valued at the appraisal). BRIDGEWATER 9886/7868/8: 0.9 ac combined (53 bu) and 72.1 ac
    // chopped on 75.1 planted read "harvested, type unknown" and the 53 bu were set aside. Only on
    // the claim page (`partialHarvest`), only on a stated grain type, never on an HMC-marked field
    // (the mark makes the whole field appraisal-valued), and only when every pass is a combine or
    // a chopper pass.
    const mixed = !!(partialHarvest && grainInsured && !hmcMarked && f.harvested
      && f.harvested.combAcres > 0 && f.harvested.chopAcres > 0
      && f.harvested.forms.every((x) => x === "combined" || x === "chopped"));
    const sliver = partialHarvest && !mixed && machineShare != null && machineShare < SLIVER_SHARE;
    // At or above FULL_HARVEST_SHARE the pass stands for the whole field (no remainder, no scaling).
    const wholeField = machineShare != null && machineShare >= FULL_HARVEST_SHARE;
    let { state, use, supersededAppraisal, machineSetAside, wrongUnitAppraisal } =
      coverageState(!!f.appraised, !!f.harvested, !!f.unmappedPass, whollyPp, { form, grainInsured, silageInsured });
    let sliverPass = false;
    if (sliver && (state === "chopped" || state === "harvest_unknown" || state === "both_chopped" || state === "both_unknown")) {
      ({ state, use, supersededAppraisal, machineSetAside, wrongUnitAppraisal } =
        coverageState(!!f.appraised, false, !!f.unmappedPass, whollyPp, { form: null, grainInsured, silageInsured }));
      sliverPass = true;
    }
    // The acres this row counts under its state, and the rest of it with no figure. Off, the
    // whole field counts under its state exactly as before. Below FULL_HARVEST_SHARE a combine
    // pass (harvested), a chopper pass on silage-insured ground (silage_chopped) and a chopper pass
    // with no appraisal on grain ground (chopped) value only the acres they covered. An appraised
    // chopped field is valued by its appraisal, so its chopper coverage never splits it.
    // ⚠ PLANTED ACRES ON THE CLAIM PAGE. A part-prevent-plant field's PP acres are the policy's
    // prevent-plant acres, not appraised, chopped or "nothing yet" ground: counted under the field's
    // state as well, they were counted twice (ANCHOR CY2026: 10.4 PP ac on three appraised fields
    // read as appraised and squeezed a 20-ac unit line down to 9.7). Off (Field Mapper), unchanged.
    let stateAcres = partialHarvest && state !== "pp" ? +plantedF.toFixed(2) : f.insuredAcres, uncoveredAcres = 0;
    let combinePart = null, splitPart = null, mixedNote = null;
    if (mixed) {
      const combAc = Math.min(plantedF, f.harvested.combAcres);
      const chopAc = Math.min(Math.max(0, plantedF - combAc), f.harvested.chopAcres);
      // ⚠ NOTHING COUNTED TWICE. An appraisal that also covers the combined ground (its acres are
      // more than the field less the combined acres, or it states no acres — a farm-level line)
      // keeps the old answer: the appraisal is the figure and the combine bushels are set aside.
      const apprAc = f.appraised && f.appraised.acres != null && !f.appraised.atFarmLevel ? f.appraised.acres : null;
      if (f.appraised && !(apprAc != null && apprAc <= plantedF - combAc + ACRE_TOL)) {
        mixedNote = `appraisal covers the ${combAc.toFixed(1)} combined ac too; appraisal used, combine set aside. Adjuster to confirm`;
      } else {
        const chopState = f.appraised ? "both_chopped" : "chopped";
        let cAc = combAc, hAc = chopAc;
        const combPrimary = cAc >= hAc;
        // Together at or above FULL_HARVEST_SHARE: the remainder joins the larger part (no scaling).
        const whole = plantedF > 0 && (cAc + hAc) / plantedF >= FULL_HARVEST_SHARE;
        combinePart = { acres: +cAc.toFixed(2), machineAcres: +f.harvested.combAcres.toFixed(1),
                        quantity: Math.round(f.harvested.combQty), quantityExact: f.harvested.combQty };
        state = combPrimary ? "harvested" : chopState;
        use = USE_OF_STATE[state] === undefined ? null : USE_OF_STATE[state];
        supersededAppraisal = false; wrongUnitAppraisal = false;
        // The chopper's tonnage is never bushels; only the combine part is counted from the machine.
        machineSetAside = !combPrimary;
        const primaryAc = combPrimary ? cAc : hAc, otherAc = combPrimary ? hAc : cAc;
        splitPart = { state: combPrimary ? chopState : "harvested", acres: +otherAc.toFixed(2) };
        // Planted acres only: a part-prevent-plant field's PP acres are neither harvested nor a hole.
        stateAcres = whole ? +Math.max(0, plantedF - otherAc).toFixed(2) : +primaryAc.toFixed(2);
        uncoveredAcres = whole ? 0 : +Math.max(0, plantedF - primaryAc - otherAc).toFixed(2);
        if (combPrimary && whole) combinePart.acres = stateAcres;
      }
    } else {
      // ⚠ AGAINST PLANTED ACRES. The covered part and the rest are both planted ground; a part-
      // prevent-plant field's PP acres are neither (they used to land in "partly harvested, no
      // figure"). At or above the threshold the field counts on its planted acres, no remainder.
      const byMachine = partialHarvest && f.harvested
        && (state === "harvested" || state === "silage_chopped" || state === "chopped");
      // (A pass on a wholly prevent-plant field — RAUBERDALE 7586/3816/1 — covers no planted acre.)
      if (byMachine && (wholeField || plantedF <= 0.005)) { stateAcres = +plantedF.toFixed(2); }
      else if (byMachine && plantedF - machineAc > 0.005) {
        stateAcres = +machineAc.toFixed(2); uncoveredAcres = +(plantedF - machineAc).toFixed(2);
      }
    }
    // ⚠ THE SILAGE LINE'S FIGURE IS A JD CHOPPER ESTIMATE, in wet tons at 35% DM, and only on a
    // silage-insured field that was chopped. A machine screening figure (Kevin: "we can use the
    // JD chopper yield data"), never production to count: silage is counted in tons as
    // harvested, by weight records, load counts or storage measurement (LASH ¶14).
    const silageEstimate = state === "silage_chopped" && f.harvested && f.harvested.chopAcres > 0
      ? { acres: +f.harvested.chopAcres.toFixed(1), dmTons: Math.round(f.harvested.chopDm),
          wetTons: Math.round(silageWetFromDm(f.harvested.chopDm)),
          wetPerAcre: +silageWetFromDm(f.harvested.chopDm / f.harvested.chopAcres).toFixed(1),
          dmPerAcre: +(f.harvested.chopDm / f.harvested.chopAcres).toFixed(2) }
      : null;
    return {
      ...f,
      // `quantityExact` rides beside the rounded figure so a SUM of many fields is rounded once,
      // not per field (RED DAWN: 60-odd half-bushel lines rounded up one by one read +6 bu).
      harvested: f.harvested ? { ...f.harvested, acres: +f.harvested.acres.toFixed(1), quantity: Math.round(f.harvested.quantity), quantityExact: f.harvested.quantity } : null,
      // ⚠ A FARM-LEVEL APPRAISAL CARRIES NO ACRES AND NO QUANTITY, and null must survive the
      // rounding — `(0).toFixed(1)` would turn "the worksheet does not say" into "zero acres",
      // which is the blank-is-not-a-zero rule this codebase applies to every other input.
      appraised: f.appraised
        ? { ...f.appraised,
            acres: f.appraised.acres == null ? null : +f.appraised.acres.toFixed(1),
            quantity: f.appraised.quantity == null ? null : Math.round(f.appraised.quantity),
            quantityExact: f.appraised.quantity == null ? null : f.appraised.quantity }
        : null,
      // ⚠ THE PASS RIDES ALONG WHATEVER THE STATE, so a field that has BOTH a grid and an
      // ungridded pass can still say so — but `state` is what the screen counts, and a gridded
      // field is counted as harvested.
      unmappedPass: f.unmappedPass || null,
      state, use, supersededAppraisal, machineSetAside, harvestForm: form,
      silageInsured, silageEstimate, wrongUnitAppraisal: !!wrongUnitAppraisal,
      stateAcres, uncoveredAcres, machineShare: machineShare == null ? null : +machineShare.toFixed(3), sliverPass,
      // A grain field the combine and the chopper both crossed (`partialHarvest`): `combinePart` is
      // the combine's own acres and bushels, `splitPart` the acres under the other part's state.
      combinePart, splitPart, mixedNote,
      // On the claim page the state counts PLANTED acres; a part-PP field's PP acres are prevent plant.
      plantedBasis: !!partialHarvest,
      hmcMarked, hmcNotApplied, hmc: hmc ? { by: hmc.set_by_label || null, at: hmc.set_at || null } : null,
      moisturePct, moistureAbove,
    };
  // ⚠ EVIDENCE FIRST. Rendered against Rogers this is 108 rows of which 85 read "nothing yet",
  // and in farm/field order the 23 that carry evidence are scattered through a wall of dashes —
  // a reader scrolls past the answer to find the question. Ordering by state puts what is known
  // at the top; the count of what is not is stated in the banner above the table, so collapsing
  // the tail costs the reader nothing.
  }).sort((a, b) => (STATE_ORDER[a.state] - STATE_ORDER[b.state])
    || (a.county || "").localeCompare(b.county) || (a.fsn || "").localeCompare(b.fsn)
    || (a.fieldId || "").localeCompare(b.fieldId));

  // `insuredAcres` per state is the acres counted UNDER that state (`stateAcres`): the whole
  // field, except a partly harvested one with `partialHarvest` on (see machineShare).
  const sum = (p, key) => rows.filter(p).reduce((s, r) => s + ((key === "insuredAcres" ? r.stateAcres : r[key]) || 0), 0);
  // The acres under one state, a mixed field's second part included (`splitPart`).
  const stAc = (st) => sum((r) => r.state === st, "insuredAcres")
    + rows.reduce((a, r) => a + (r.splitPart && r.splitPart.state === st ? r.splitPart.acres : 0), 0);
  // The rest of a partly harvested grain field (a partly chopped silage field's rest is silage-side).
  const grainPartial = (r) => r.state !== "silage_chopped" && r.uncoveredAcres > 0.005;
  const byState = (s) => rows.filter((r) => r.state === s);

  return {
    crop: CROP, cropYear: CY,
    rows,
    totals: {
      fields: rows.length,
      insuredAcres: +rows.reduce((s, r) => s + r.insuredAcres, 0).toFixed(1),
      appraisedOnly: byState("appraised").length,
      harvestedOnly: byState("harvested").length,
      both: byState("both").length,
      // A COMBINE PASS IS ON FILE AND ITS MAP IS NOT. Counted apart from both "harvested"
      // (which carries a measured per-field figure) and "nothing yet" (which says the combine
      // has not been there), because it is neither.
      unmapped: byState("unmapped").length,
      unmappedAcres: +sum((r) => r.state === "unmapped", "insuredAcres").toFixed(1),
      // THE FINDING, and its acres — a count of fields understates a 200-acre hole.
      neither: byState("neither").length,
      neitherAcres: +sum((r) => r.state === "neither", "insuredAcres").toFixed(1),
      // ── ACRES PER STATE, because the question is asked in acres ────────────
      // Kevin, 2026-10-06, on the claim screen: *"in the case of Mulligan where it looks
      // like every insured acre may be appraised, we know not to go looking for other
      // production, but right now it's very ambiguous and I can't really tell if every
      // insured acre was appraised."* Every state above was a COUNT OF FIELDS, and a count
      // cannot answer that — eleven accounted fields out of twelve says nothing about
      // whether the twelfth is 2 acres or 200. `neitherAcres` already existed for exactly
      // that reason ("a count of fields understates a 200-acre hole"); the other states
      // simply never got the same treatment.
      appraisedOnlyAcres: +sum((r) => r.state === "appraised", "insuredAcres").toFixed(1),
      harvestedOnlyAcres: +stAc("harvested").toFixed(1),
      bothAcres: +sum((r) => r.state === "both", "insuredAcres").toFixed(1),
      // ── appraised AND chopped / harvest type unknown — the APPRAISAL is the figure ──
      bothChopped: byState("both_chopped").length,
      bothChoppedAcres: +stAc("both_chopped").toFixed(1),
      bothUnknown: byState("both_unknown").length,
      bothUnknownAcres: +sum((r) => r.state === "both_unknown", "insuredAcres").toFixed(1),
      // ── chopped (or harvested in a form we cannot tell) with NO appraisal, on a grain
      // policy: no grain figure exists for these acres. A hole, named. ──
      hmcMarked: rows.filter((r) => r.hmcMarked).length,
      hmcNotApplied: rows.filter((r) => r.hmcNotApplied).length,
      // An HMC field whose state is still 'chopped' has NO appraisal: a hole, named (claimTodos).
      hmcNoAppraisal: rows.filter((r) => r.hmcMarked && r.state === "chopped").length,
      hmcNoAppraisalAcres: +sum((r) => r.hmcMarked && r.state === "chopped", "insuredAcres").toFixed(1),
      // Combined above the moisture line and NOT marked: flagged, still counted as combined.
      moistureFlagged: rows.filter((r) => r.moistureAbove && !r.hmcMarked).length,
      moistureFlaggedAcres: +sum((r) => r.moistureAbove && !r.hmcMarked, "insuredAcres").toFixed(1),
      chopped: byState("chopped").length,
      choppedAcres: +stAc("chopped").toFixed(1),
      harvestUnknown: byState("harvest_unknown").length,
      harvestUnknownAcres: +sum((r) => r.state === "harvest_unknown", "insuredAcres").toFixed(1),
      // ── INSURED AS SILAGE (026): its own line, in TONS, never in any bushel figure ──
      silageChopped: byState("silage_chopped").length,
      silageChoppedAcres: +sum((r) => r.state === "silage_chopped", "insuredAcres").toFixed(1),
      silageWetTons: rows.reduce((a, r) => a + (r.silageEstimate ? r.silageEstimate.wetTons : 0), 0),
      silageDmTons: rows.reduce((a, r) => a + (r.silageEstimate ? r.silageEstimate.dmTons : 0), 0),
      silageEstAcres: +rows.reduce((a, r) => a + (r.silageEstimate ? r.silageEstimate.acres : 0), 0).toFixed(1),
      silageNone: byState("silage_none").length,
      // + the unchopped rest of a partly chopped silage field (partialHarvest).
      silageNoneAcres: +(sum((r) => r.state === "silage_none", "insuredAcres")
        + rows.reduce((a, r) => a + (r.state === "silage_chopped" ? r.uncoveredAcres : 0), 0)).toFixed(1),
      // Partly combined grain fields: the rest of each field has no figure (partialHarvest).
      partialFields: rows.filter(grainPartial).length,
      partialUncoveredAcres: +rows.reduce((a, r) => a + (grainPartial(r) ? r.uncoveredAcres : 0), 0).toFixed(1),
      // Grain fields the combine and the chopper both crossed, split into their two parts.
      mixedFields: rows.filter((r) => r.splitPart).length,
      mixedSetAside: rows.filter((r) => r.mixedNote).length,
      sliverPasses: rows.filter((r) => r.sliverPass).length,
      // Bushel appraisals on silage-insured fields: FLAGGED (wrong unit), never counted.
      silageBuAppraisals: rows.filter((r) => r.wrongUnitAppraisal).length,
      silageBuAppraisalAcres: +rows.reduce((a, r) => a + (r.wrongUnitAppraisal && r.appraised && r.appraised.acres ? r.appraised.acres : 0), 0).toFixed(1),
      silageBuAppraisalBu: rows.reduce((a, r) => a + (r.wrongUnitAppraisal && r.appraised && r.appraised.quantity ? r.appraised.quantity : 0), 0),
      // ⚠ WHAT "ACCOUNTED FOR" MEANS, AND THE TWO IT DELIBERATELY EXCLUDES. An acre is
      // accounted for when something has VALUED it: an appraisal, a machine pass, or both
      // (where the machine supersedes the appraisal — see coverageState). `unmapped` is
      // NOT accounted: a combine pass is on file and its map is not, so we know the field
      // was harvested and still hold no figure for it. Folding that in would report a
      // production figure we do not have, and `neither` is the hole itself. Both are
      // carried out separately so the three can never be collapsed into one tidy total.
      // ⚠ PREVENT PLANT COUNTS AS ACCOUNTED, because it IS. These acres are settled — the
      // policy already knows what happened on them — and leaving them out of the numerator
      // while they sit in the denominator is what makes a fully-walked farm read short.
      // They are also reported on their OWN line so nobody reads them as production.
      // On the claim page a part-prevent-plant field's PP acres are prevent plant too (its state
      // counts its planted acres only), so the map's planted acres are insured less ALL PP.
      preventPlantAcres: +(sum((r) => r.state === "pp", "insuredAcres")
        + rows.reduce((a, r) => a + (r.plantedBasis && r.state !== "pp" ? (r.ppAcres || 0) : 0), 0)).toFixed(1),
      preventPlantFields: byState("pp").length,
      accountedAcres: +(sum((r) => ACCOUNTED_STATES.includes(r.state), "insuredAcres")
        + rows.reduce((a, r) => a + (r.splitPart && ACCOUNTED_STATES.includes(r.splitPart.state) ? r.splitPart.acres : 0), 0)).toFixed(1),
      supersededAppraisals: byState("both").length,
      // How many fields are accounted for only at the farm number — a WEAKER answer than a
      // field match, so it is counted separately rather than folded into `appraisedOnly`.
      appraisedAtFarmLevel: rows.filter((r) => r.appraised && r.appraised.atFarmLevel).length,
    },
    // Named, never dropped — and the two lists are different facts. `farmLevel` is production
    // we HAVE, attributed to a farm number whose acres reconcile; `unplaced` is production we
    // cannot attribute at all, which is the one that needs a human.
    // The passes themselves, so the screen can name the operation rather than the field —
    // one pass covering two fields is ONE combine run and reads as two rows otherwise.
    unmappedPasses,
    unmappedPassCount: unmappedPasses.length,
    // ⚠ A PASS NOTHING CAN PLACE IS STILL REPORTED. Silently dropping it is how 6,715 acres of
    // real harvest stayed invisible; the reader needs to know the data exists even where this
    // rule cannot tie it to an insured field.
    unplacedPasses,
    unplacedPassCount: unplacedPasses.length,
    farmLevelAppraisals,
    farmLevelCount: farmLevelAppraisals.length,
    // Reported, never absorbed — see overAppraisedFsns. The fields are covered; the acreage
    // discrepancy is its own line on the panel.
    overAppraisedFsns,
    overAppraisedCount: overAppraisedFsns.length,
    overAppraisedAcres: +overAppraisedFsns.reduce((t, r) => t + r.excessAcres, 0).toFixed(1),
    // Counted at the unit (claimAccounting), never on a field: see `countyOfLine`.
    unplacedAppraisals: stillUnplaced,
    unplacedCount: stillUnplaced.length,
    // Bushel lines on silage-insured ground no 026 field accepted: flagged, never counted.
    unplacedSilageBu,
  };
}

// ⚠ WHAT THIS RULE READS, SO A CALLER'S QUERY CAN BE CHECKED AGAINST IT. A column the rule
// reads and the query never SELECTED does not throw — PostgREST simply omits it, the field is
// `undefined`, and the rule answers "no appraisal places anywhere" for the whole book while
// looking exactly like a farm with no appraisals. That is the class `check-query-contract`
// exists for, and `bfrNeedsColumns` is the precedent.
//
// `tract` is the load-bearing one and the easiest to leave out: without it EVERY appraisal
// looks like a no-tract row, so the full key never fires and the two rows that genuinely
// carry a tract fall through to the acreage tier — which is usually right, and silently
// stops being a key match.
const NEEDS_COLUMNS = Object.freeze({
  // `pp_acres` decides whether a field with no appraisal is a HOLE or is settled ground.
  // Omitted, every prevent-planted field reads as production nobody has stated.
  // `unit_number` + `sub_unit` are the UNIT tier's key. Omitted, every appraisal under a
  // blank-farm-number header places nowhere and the panel looks exactly as it did before.
  // `type_code` decides whether the grain rule applies (016 grain / 026 silage). Omitted, no
  // field is grain-insured and a chopper's tonnage is reported as production again.
  policy_fields: ["clu_id", "fsn", "tract", "field_id", "crop", "crop_year", "acres", "pp_acres",
                  "unit_number", "sub_unit", "type_code"],
  // `type_code` on the LINE is the type guard: a 016 line matches only 016 fields, a 026 line
  // only 026. Omitted, every line reads as untyped and matches either (the pre-guard shape).
  silage_appraisals: ["fsn", "tract", "field_no", "field_name", "crop", "crop_year",
                      "county", "appraised_acres", "appraised_bu", "unit_number", "type_code"],
  // `jd_field_id` is what ties a CLU to the John Deere field an UNGRIDDED pass belongs to.
  // Omit it and every unmapped pass falls to `unplacedPasses` — the rule still runs, reports
  // nothing placed, and looks exactly like a book with no ungridded passes in it.
  jd_field_ftf: ["clu_id", "crop", "metric", "acres", "mean_value", "operation_date",
                 "jd_field_id", "jd_operation_id"],
  jd_field_operations: ["jd_operation_id", "jd_field_id", "field_name", "crop",
                        "operation_start", "area_acres", "dry_yield", "yield_unit", "moisture_pct"],
});

/**
 * acreAccounting({ totals, policyInsured, policyPp, countyAppraised })
 *
 * The "N of M planted ac accounted for" ring on the Claims Manager, as ONE rule with its
 * arithmetic shown, so the ring and the warning under it cannot disagree.
 *
 * ⚠ RED DAWN FARMING LLC CY2026 read **6,985.4 of 5,746.9 planted ac accounted for** and,
 * under it, **"-1,238.5 ac still need a production figure"**. Every planted acre on that
 * policy already has a field row (map planted = policy planted = 5,746.9), and the ring still
 * added a slice of **1,735.6 ac "appraised, not matched to a field"** — appraisal acres on
 * file (3,379.6) minus appraisal acres placed on a field (1,644.0). That slice exists for
 * MULLIGAN, whose 24.4 planted acres have NO field row and were walked: an unplaced appraisal
 * can stand in for acres the field map does not carry, and for nothing else. Where the map
 * already carries every planted acre, an appraisal we cannot tie to a field is a MATCHING
 * GAP (or an acreage finding), never more accounted ground — counting it twice is how a
 * farm reads 122% accounted with a negative hole.
 *
 * So:
 *   apprNotPlaced   = min(appraisal acres beyond the map, planted acres with no field row)
 *   apprUnusable    = the rest — REPORTED, never drawn as accounted
 *   accounted       = field-accounted + apprNotPlaced, and never more than planted
 *   stillNeed       = planted − accounted, never negative
 * and the identity the checker holds: every slice of the ring sums to the ring total.
 */
function acreAccounting({ totals, policyInsured = 0, policyPp = 0, countyAppraised = 0 } = {}) {
  const t = totals || {};
  const r1 = (v) => +((Number(v) || 0)).toFixed(1);
  const mapInsured = r1(t.insuredAcres);
  const mapPp = r1(t.preventPlantAcres);
  const policyPlanted = r1(r1(policyInsured) - r1(policyPp));
  const mapPlanted = r1(mapInsured - mapPp);
  const denom = policyPlanted > 0 ? policyPlanted : mapPlanted;
  // Only a policy LARGER than the map is a gap; a map larger than the policy is a different
  // finding and is never turned into a negative here.
  const unmapped = policyPlanted > mapPlanted + 0.05 ? r1(policyPlanted - mapPlanted) : 0;
  // Appraisal placed on a field: appraised only, combined over it, or chopped / unknown over it.
  const mapAppraised = r1((t.appraisedOnlyAcres || 0) + (t.bothAcres || 0)
                          + (t.bothChoppedAcres || 0) + (t.bothUnknownAcres || 0));
  const apprBeyondMap = r1(countyAppraised) > mapAppraised + 0.05 ? r1(r1(countyAppraised) - mapAppraised) : 0;
  const apprNotPlaced = r1(Math.min(apprBeyondMap, unmapped));
  const apprUnusable = r1(apprBeyondMap - apprNotPlaced);
  const noFieldNoAppraisal = r1(Math.max(0, unmapped - apprNotPlaced));
  // ACCOUNTED_STATES less prevent plant (not in a planted-acre question). A chopped field with
  // no appraisal is NOT here: on a grain policy its tonnage values nothing.
  // Silage-insured acres with a JD chopper estimate are accounted ON THE SILAGE LINE (tons).
  const fieldAccounted = r1((t.appraisedOnlyAcres || 0) + (t.harvestedOnlyAcres || 0) + (t.bothAcres || 0)
                            + (t.bothChoppedAcres || 0) + (t.bothUnknownAcres || 0) + (t.silageChoppedAcres || 0));
  const rawAccounted = r1(fieldAccounted + apprNotPlaced);
  const accounted = r1(Math.min(denom, rawAccounted));
  const overAccounted = r1(Math.max(0, rawAccounted - denom));
  const stillNeed = r1(Math.max(0, denom - accounted));
  const segments = [
    ["appraised", r1(t.appraisedOnlyAcres)],
    ["harvested", r1(t.harvestedOnlyAcres)],
    ["appraised + combined (combine used)", r1(t.bothAcres)],
    ["appraised + chopped (appraisal used)", r1(t.bothChoppedAcres)],
    ["appraised + harvest type unknown (appraisal used)", r1(t.bothUnknownAcres)],
    ["pass on file, no map", r1(t.unmappedAcres)],
    ["nothing yet", r1(t.neitherAcres)],
    ["partly harvested — rest of the field has no figure", r1(t.partialUncoveredAcres)],
    ["chopped, not appraised — no grain figure", r1(t.choppedAcres)],
    ["harvested, type unknown — no grain figure", r1(t.harvestUnknownAcres)],
    ["insured as silage, chopped — JD chopper estimate (tons)", r1(t.silageChoppedAcres)],
    ["insured as silage — no silage figure yet", r1(t.silageNoneAcres)],
    ["appraised, not matched to a field", apprNotPlaced],
    ["insured, but no field on the map", noFieldNoAppraisal],
  ].filter(([, a]) => a > 0.05);
  const segmentSum = r1(segments.reduce((s2, [, a]) => s2 + a, 0));
  // ⚠ `done` IS THE GRAIN ANSWER (Kevin, 2026-11-03: "It all depends on how the acres are insured").
  // Silage-insured (026) acres waiting on tons belong to the silage line and its own status
  // (`silageDone`); they no longer hold the grain ring open (audit 2026-11-03 #10).
  const done = (t.neitherAcres || 0) <= 0.05 && (t.unmappedAcres || 0) <= 0.05 && noFieldNoAppraisal <= 0.05
    && (t.choppedAcres || 0) <= 0.05 && (t.harvestUnknownAcres || 0) <= 0.05
    && (t.partialUncoveredAcres || 0) <= 0.05;
  const silageDone = (t.silageNoneAcres || 0) <= 0.05;
  return {
    denom, mapInsured, mapPlanted, policyPlanted, policyPp: r1(policyPp), unmapped,
    countyAppraised: r1(countyAppraised), mapAppraised, apprBeyondMap, apprNotPlaced, apprUnusable,
    noFieldNoAppraisal, fieldAccounted, accounted, overAccounted, stillNeed,
    pct: denom > 0 ? Math.max(0, Math.min(1, accounted / denom)) : 0,
    segments, segmentSum, ringTotal: Math.max(denom, segmentSum), done, silageDone,
  };
}

// ⚠ ONE PLACE THAT REMOVES A MARKED FIELD'S COMBINE BUSHELS FROM EVERY OTHER MACHINE FIGURE ON THE PAGE
// (the monitor lane in productionEvidence, the "Scale tickets / records on file" line, the county John
// Deere "use" suggestion). A field marked high-moisture corn is valued by its appraisal, so its combine
// rows must not reach any of them. `key` is the row's field identity (clu_id).
function dropHmcRows(rows, hmcFields, key = "clu_id") {
  const marked = new Set();
  for (const h of hmcFields || []) { const id = k(h && h.field_key); if (id) marked.add(id); }
  if (!marked.size) return rows || [];
  return (rows || []).filter((r) => !marked.has(k(r && r[key])));
}

module.exports = { dropHmcRows, HMC_MOISTURE_PCT, FULL_HARVEST_SHARE, SLIVER_SHARE, buildFieldCoverage, coverageState, harvestFormOf, acreAccounting, ACCOUNTED_STATES,
                   MATCH, NEEDS_COLUMNS };
